import { styled } from '@mui/material/styles';
import {
  Box,
  Typography,
  Button,
  OutlinedInput,
  FormControl,
  ButtonProps,
  Link,
} from '@mui/material';

export const AuthFormContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'smScreen' && prop !== 'mdScreen',
})<{
  smScreen?: boolean;
  mdScreen?: boolean;
}>(({ smScreen, mdScreen }) => ({
  margin: smScreen ? '8px' : '16px 32px',
  width: '100%',
  maxWidth: mdScreen ? '100%' : 600,
  marginLeft: mdScreen ? '4px' : 'auto', // mx: 0.5 -> 0.5 * 8 = 4px
  marginRight: mdScreen ? '4px' : 'auto',
  marginTop: smScreen ? '12px' : '8px', // my: 1 -> 8px, 1.5 -> 12px
  marginBottom: smScreen ? '12px' : '8px',
  maxHeight: smScreen ? 'calc(100vh - 80px)' : 'calc(100vh - 100px)',
  overflow: 'auto',
  paddingTop: smScreen ? '24px' : '16px', // py: 2 -> 16, 3 -> 24
  paddingBottom: smScreen ? '24px' : '16px',
  backgroundColor: 'white',
  borderRadius: '8px',
  border: '1px solid',
  borderColor: 'var(--border)',
  boxShadow: smScreen ? 'none' : '0px 0px 10px rgba(0,0,0,0.1)',
}));

export const AuthTitle = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'smScreen' && prop !== 'mdScreen',
})<{ smScreen?: boolean; mdScreen?: boolean }>(({ smScreen, mdScreen }) => ({
  marginLeft: smScreen ? 0 : 'clamp(16px, 10%, 64px)',
  marginTop: smScreen ? '12px' : mdScreen ? '12px' : '16px', // mt: 1 -> 8, 1.5 -> 12, 2 -> 16
  fontFamily: 'Lufga',
  fontSize: smScreen ? '22px' : '32px',
  fontWeight: '500',
  color: 'var(--primary-blue)',
}));

export const AuthFieldsContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'smScreen',
})<{ smScreen?: boolean }>(({ smScreen }) => ({
  marginLeft: smScreen ? 0 : 'clamp(16px, 10%, 64px)',
  marginTop: smScreen ? '20px' : '16px', // mt: 2 -> 16, 2.5 -> 20
  marginBottom: smScreen ? '20px' : '16px',
}));

export const AuthButton = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'smScreen',
})<{ smScreen?: boolean }>(({ smScreen }) => ({
  marginLeft: smScreen ? 0 : 'clamp(16px, 10%, 64px)',
  marginTop: smScreen ? '16px' : '12px', // mt: 1.5 -> 12, 2 -> 16
  width: smScreen ? '100%' : 'min(450px, 90%)',
  fontFamily: 'Outfit',
  fontSize: '16px',
  fontWeight: '500',
}));

export const BackButton = styled(Button)({
  width: 45,
  height: 45,
  minWidth: 45,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginTop: '32px',
  marginLeft: '64px',
});

export const ChevronIcon = styled('i')({
  fontSize: '36px',
});

export const VerificationImage = styled('img')({
  width: '195px',
  height: '180px',
  display: 'block',
  margin: '0 auto',
  marginTop: '48px',
});

export const CenteredButtonContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  marginTop: '40px',
  marginBottom: '40px',
});

export const AuthInput = styled(OutlinedInput)<{ smScreen?: boolean }>(
  ({ smScreen }) => ({
    height: '40px',
    width: smScreen ? '100%' : 'min(450px, 90%)',
    backgroundColor: 'var(--text-bg)',
    border: '1px solid',
    borderColor: 'var(--border)',
    borderRadius: '4px',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--border)',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--border)',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--border)',
    },
    '& input': {
      fontFamily: 'Outfit',
      fontSize: '16px',
    },
    overflow: 'hidden',
    '& input:-webkit-autofill': {
      backgroundColor: 'var(--bg-color)',
      WebkitBoxShadow: '0 0 0 30px var(--bg-color) inset',
    },
    '& input:-moz-autofill': {
      backgroundColor: 'var(--bg-color)',
      boxShadow: '0 0 0 30px var(--bg-color) inset',
    },
    '& input:-ms-autofill': {
      backgroundColor: 'var(--bg-color)',
      boxShadow: '0 0 0 30px var(--bg-color) inset',
    },
  })
);

export const AuthFormControl = styled(FormControl)({
  margin: 0,
  width: '100%',
});

export const InstructionText = styled(Typography)({
  fontFamily: 'Nunito Sans',
  fontWeight: '400',
  fontSize: '14px',
  color: 'var(--instructions)',
  marginBottom: '8px',
});

export const AuthLabel = styled(Typography)({
  fontFamily: 'Outfit',
  fontWeight: '400',
  fontSize: '16px',
  color: 'var(--primary-navy)',
  marginTop: '16px',
  marginBottom: '16px',
});

export const AuthLinkContainer = styled(Box)<{ smScreen?: boolean }>(
  ({ smScreen }) => ({
    marginLeft: smScreen ? 0 : 'clamp(16px, 10%, 64px)',
    marginTop: smScreen ? '16px' : '32px',
    display: 'flex',
    justifyContent: smScreen ? 'center' : 'space-between',
    alignItems: 'center',
    width: smScreen ? '100%' : 'min(450px, 90%)',
    textAlign: smScreen ? 'center' : 'left',
  })
);

export const AuthErrorText = styled(Box)({
  fontFamily: 'Outfit',
  fontWeight: '400',
  fontSize: '14px',
  color: 'var(--error)',
  display: 'flex',
  alignItems: 'center',
  marginLeft: 0,
  marginTop: '8px',
});

export const AuthErrorIcon = styled(Box)({
  fontSize: '16px',
  color: 'var(--error)',
  marginRight: '8px',
});

interface ResendButtonProps extends ButtonProps {
  width?: string;
}

export const ResendButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'width',
})<ResendButtonProps>(({ width }) => ({
  width: width || '140px',
}));

export const VerificationPageContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'xsPadding' && prop !== 'smPadding',
})<{ xsPadding?: number; smPadding?: number }>(({ xsPadding, smPadding }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'center',
  minHeight: '100vh',
  backgroundColor: '#FFFFFF',
  padding: smPadding
    ? `${smPadding * 8}px`
    : xsPadding
    ? `${xsPadding * 8}px`
    : '16px',
  overflowY: 'auto',
}));

export const AuthFooterContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'smScreen' && prop !== 'mdScreen',
})<{ smScreen?: boolean; mdScreen?: boolean }>(({ smScreen, mdScreen }) => ({
  marginLeft: smScreen ? 0 : mdScreen ? '16px' : 'clamp(16px, 10%, 64px)',
  marginTop: smScreen ? '20px' : mdScreen ? '24px' : '24px', // mt: 2 -> 16, 2.5 -> 20, 3 -> 24
  marginBottom: smScreen ? '16px' : '8px', // mb: 1 -> 8, 2 -> 16
  display: 'flex',
  flexDirection: smScreen ? 'column' : 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: smScreen ? '12px' : 0,
  width: smScreen ? '100%' : mdScreen ? 'calc(100% - 16px)' : 'min(450px, 90%)',
}));

export const AuthFooterText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'smScreen',
})<{ smScreen?: boolean }>(({ smScreen }) => ({
  fontSize: smScreen ? '12px' : '14px',
  fontFamily: 'Outfit',
  fontWeight: '300',
  color: '#0B2330',
}));

export const AuthFooterLink = styled(Link, {
  shouldForwardProp: (prop) => prop !== 'smScreen',
})<{ smScreen?: boolean }>(({ smScreen }) => ({
  fontSize: smScreen ? '12px' : '14px',
  fontFamily: 'Lufga',
  fontWeight: '500',
  color: 'var(--subtext)',
  textDecoration: 'none',
  '&:hover': {
    textDecoration: 'underline',
  },
}));

export const VerificationTimerText = styled(Typography)({
  fontFamily: 'Outfit',
  fontWeight: '400',
  fontSize: '18px',
  color: 'var(--primary-blue)',
  marginBottom: '8px',
  textAlign: 'center',
});
